<script>
import events from './events'
import { i18nRender } from '@/locales'

// 首页
const homeRoute = {
  fullPath: '/index',
  path: '/index',
  meta: { title: '首页' }
}

// ⭐ 片商角色 key
const FILM_ROLE_KEY = 'film_user'

// ✅ 片商 Tab 英文映射（按你系统实际 Tab 名继续补）
const TAB_EN_MAP = {
  '首页': 'Home',
  '短剧统计': 'Drama Analytics',
  '短剧': 'Short Dramas',
  '单集数据详情': 'Episode Details',
  '查看单集统计': 'Episode Analytics'
}

// ✅ 右键菜单/提示语文案
const UI_TEXT = {
  zh: {
    closeThat: '关闭当前标签',
    closeRight: '关闭右侧',
    closeLeft: '关闭左侧',
    closeAll: '关闭全部',
    lastTab: '这是最后一个标签了, 无法被关闭',
    noLeft: '左侧没有标签',
    noRight: '右侧没有标签'
  },
  en: {
    closeThat: 'Close Current',
    closeRight: 'Close Right',
    closeLeft: 'Close Left',
    closeAll: 'Close All',
    lastTab: 'This is the last tab and cannot be closed',
    noLeft: 'No tabs on the left',
    noRight: 'No tabs on the right'
  }
}

export default {
  name: 'MultiTab',
  data () {
    return {
      pages: [],
      activeIndex: 0
    }
  },

  computed: {
    roles () {
      return (this.$store && this.$store.getters && this.$store.getters.roles) || []
    },
    isFilmUser () {
      return this.roles.includes(FILM_ROLE_KEY)
    },
    lang () {
      return this.isFilmUser ? 'en' : 'zh'
    }
  },

  created () {
    // bind event
    events.$on('open', val => {
      if (!val) throw new Error(`multi-tab: open tab ${val} err`)
      this.activeIndex = val
    }).$on('close', val => {
      if (!val) {
        this.closeThat(this.activeIndex)
        return
      }
      this.closeThat(val)
    }).$on('rename', ({ key, name }) => {
      try {
        const item = this.pages.find(item => item.path === key)
        item.meta.customTitle = name
        this.$forceUpdate()
      } catch (e) {}
    })

    // ✅ 先按“默认非片商”把首页塞进去（后续 roles 异步回来会自动同步）
    if (homeRoute.path !== this.$route.path) {
      this.pages.push(homeRoute)
    }

    // 当前路由无论是谁都要加
    this.pages.push(this.$route)

    // 根据当前 roles 同步一次
    this.syncHomeTab()

    this.selectedLastPath()
    this.$store.pages = this.pages
  },

  methods: {
    t (k) {
      const pack = UI_TEXT[this.lang] || UI_TEXT.zh
      return pack[k] || k
    },

    // ✅ roles 异步回来后也能正确增删首页 tab
    syncHomeTab () {
      const homeIdx = this.pages.findIndex(p => p.path === homeRoute.path)

      if (this.isFilmUser) {
        // 片商：不显示首页 tab
        if (homeIdx >= 0) {
          this.pages.splice(homeIdx, 1)
          if (this.activeIndex > homeIdx) this.activeIndex--
          if (this.activeIndex === homeIdx && this.pages.length) {
            this.selectedLastPath()
          }
        }
      } else {
        // 非片商：确保首页在第一个（且不重复）
        if (homeRoute.path !== this.$route.path) {
          if (homeIdx < 0) {
            this.pages.unshift(homeRoute)
            this.activeIndex++
          } else if (homeIdx > 0) {
            const [h] = this.pages.splice(homeIdx, 1)
            this.pages.unshift(h)
            if (this.activeIndex === homeIdx) this.activeIndex = 0
            else if (this.activeIndex < homeIdx) this.activeIndex++
          }
        } else {
          // 当前就在首页时，不强行插入固定首页 tab
          if (homeIdx >= 0 && this.pages.length > 1) {
            this.pages.splice(homeIdx, 1)
            if (this.activeIndex > homeIdx) this.activeIndex--
          }
        }
      }

      this.$store.pages = this.pages
      this.$forceUpdate()
    },

    // ✅ Tab 标题：片商英文，非片商保持原样
    getTabTitle (page) {
      const meta = (page && page.meta) || {}
      const raw = meta.customTitle || meta.title || page.name || page.path || ''

      // 非片商：保持原逻辑（走 i18nRender）
      if (!this.isFilmUser) {
        return i18nRender(raw) || raw
      }

      // 片商：优先路由 meta.titleEn（如果你有），否则走映射表
      return meta.titleEn || TAB_EN_MAP[raw] || raw
    },

    onEdit (targetKey, action) {
      this[action](targetKey)
    },

    remove (targetIndex) {
      this.$store.dispatch('tagsView/delCachedView', this.pages[targetIndex])
      this.pages.splice(targetIndex, 1)

      if (this.activeIndex === targetIndex) {
        this.selectedLastPath()
      } else if (this.activeIndex > targetIndex) {
        this.activeIndex--
      }
      this.$store.pages = this.pages
    },

    selectedLastPath () {
      this.activeIndex = this.pages.length - 1
      const page = this.pages[this.activeIndex]
      if (page && page.name) {
        this.$store.dispatch('tagsView/addCachedView', page)
      }
      if (page) {
        this.$router.push({ path: page.path, query: page.query, params: page.params })
      }
    },

    // content menu
    closeThat (e) {
      if (this.pages.length > 1) {
        this.remove(e)
      } else {
        this.$message.info(this.t('lastTab'))
      }
    },

    closeLeft (e) {
      // 片商没有固定首页 tab，从 0 开始关；非片商保留首页，从 1 开始关
      const start = this.isFilmUser ? 0 : 1
      if (e > start) {
        for (let index = start; index < e; index++) {
          this.remove(start)
        }
      } else {
        this.$message.info(this.t('noLeft'))
      }
    },

    closeRight (e) {
      if (e < (this.pages.length - 1)) {
        while (e < this.pages.length - 1) {
          this.remove(e + 1)
        }
      } else {
        this.$message.info(this.t('noRight'))
      }
    },

    closeAll () {
      // 片商：全关；非片商：保留首页
      const keep = this.isFilmUser ? 0 : 1
      while (this.pages.length > keep) {
        this.remove(keep)
      }
    },

    closeMenuClick (key, route) {
      this[key](route)
    },

    renderTabPaneMenu (e, index) {
      const keepHome = !this.isFilmUser
      return (
          <a-menu {...{ on: { click: ({ key }) => { this.closeMenuClick(key, index) } } }}>
            <a-menu-item disabled={keepHome ? index === 0 : false} key="closeThat">{this.t('closeThat')}</a-menu-item>
            <a-menu-item disabled={this.pages.length === index + 1} key="closeRight">{this.t('closeRight')}</a-menu-item>
            <a-menu-item disabled={keepHome ? index <= 1 : index <= 0} key="closeLeft">{this.t('closeLeft')}</a-menu-item>
            <a-menu-item disabled={this.pages.length === 1} key="closeAll">{this.t('closeAll')}</a-menu-item>
          </a-menu>
      )
    },

    // render
    renderTabPane (page, index) {
      const menu = this.renderTabPaneMenu(page.path, index)
      const title = this.getTabTitle(page)

      return (
          <a-dropdown overlay={menu} trigger={['contextmenu']}>
            <span style={{ userSelect: 'none' }}>{ title }</span>
          </a-dropdown>
      )
    }
  },

  watch: {
    // ✅ roles 变化（异步加载）时，同步首页 tab + 触发重渲染
    isFilmUser: {
      immediate: true,
      handler () {
        this.syncHomeTab()
      }
    },

    '$route': function (newVal) {
      let currentIndex = this.pages.findIndex((item) => item.path === newVal.path)
      if (currentIndex < 0) {
        this.pages.push(newVal)
        currentIndex = this.pages.length - 1
      } else {
        const page = this.pages[currentIndex]
        if (page.fullPath !== newVal.fullPath || JSON.stringify(page.params) !== JSON.stringify(newVal.params)) {
          this.pages.splice(currentIndex, 1, newVal)
        }
      }
      this.activeIndex = currentIndex
      this.$store.pages = this.pages
    },

    activeIndex: function (index) {
      const page = this.pages[index]
      if (page && page.name) {
        this.$store.dispatch('tagsView/addCachedView', page)
      }
      if (page) {
        this.$router.push({ path: page.path, query: page.query, params: page.params })
      }
    }
  },

  render () {
    const { onEdit, $data: { pages } } = this

    const panes = pages.map((page, index) => {
      return (
          <a-tab-pane
              style={{ height: 0 }}
              tab={this.renderTabPane(page, index)}
              key={index}
              closable={page.path !== homeRoute.path}
          />
      )
    })

    return (
        <div class="ant-pro-multi-tab">
          <div class="ant-pro-multi-tab-wrapper">
            <a-tabs
                hideAdd
                type={'editable-card'}
                v-model={this.activeIndex}
                tabBarStyle={{ background: '#FFF', margin: 0, paddingLeft: '16px', paddingTop: '1px' }}
                {...{ on: { edit: onEdit } }}
            >
              {panes}
            </a-tabs>
          </div>
        </div>
    )
  }
}
</script>
